import { useEffect, useMemo, useState } from "react";
import type { ScheduleProposalRequest } from "../../types/gigTypes";
import {
  formatDateTimeInTimeZone,
  resolveGigTimeZone,
  toDateTimeLocalInputInTimeZone,
  toMinutePrecision,
  toZonedIsoString,
} from "../../utils/DateUtils";
import Button from "../ui/Button/Button";
import Modal from "../ui/Modal/Modal";

type ScheduleProposalFormProps = {
  subtitle: string;
  initialStartTime?: string | null;
  initialEndTime?: string | null;
  availabilityStartTime?: string | null;
  availabilityEndTime?: string | null;
  timeZone?: string | null;
  allowOutsideAvailabilityWindow?: boolean;
  submitError?: string | null;
  isSubmitting: boolean;
  onCancel?: () => void;
  onSubmit: (payload: ScheduleProposalRequest) => Promise<void>;
};

type ScheduleProposalModalProps = ScheduleProposalFormProps & {
  isOpen: boolean;
  title: string;
  onClose: () => void;
};

export const ScheduleProposalForm = ({
  subtitle,
  initialStartTime,
  initialEndTime,
  availabilityStartTime,
  availabilityEndTime,
  timeZone,
  allowOutsideAvailabilityWindow = false,
  submitError,
  isSubmitting,
  onCancel,
  onSubmit,
}: ScheduleProposalFormProps) => {
  const [startTime, setStartTime] = useState("");
  const [error, setError] = useState<string | null>(null);
  const effectiveTimeZone = resolveGigTimeZone(timeZone);

  useEffect(() => {
    setStartTime(toDateTimeLocalInputInTimeZone(initialStartTime ?? null, effectiveTimeZone));
    setError(null);
  }, [effectiveTimeZone, initialEndTime, initialStartTime]);

  useEffect(() => {
    if (submitError) {
      setError(submitError);
    }
  }, [submitError]);

  const currentMinuteInput = useMemo(
    () => toDateTimeLocalInputInTimeZone(toMinutePrecision(new Date()).toISOString(), effectiveTimeZone),
    [effectiveTimeZone],
  );
  const availabilityStartInput = useMemo(
    () => toDateTimeLocalInputInTimeZone(availabilityStartTime ?? null, effectiveTimeZone),
    [availabilityStartTime, effectiveTimeZone],
  );
  const availabilityEndInput = useMemo(
    () => toDateTimeLocalInputInTimeZone(availabilityEndTime ?? null, effectiveTimeZone),
    [availabilityEndTime, effectiveTimeZone],
  );
  const availabilityWindowLabel = useMemo(() => {
    const formattedStart = formatDateTimeInTimeZone(availabilityStartTime ?? null, effectiveTimeZone, null);
    const formattedEnd = formatDateTimeInTimeZone(availabilityEndTime ?? null, effectiveTimeZone, null);

    if (!formattedStart || !formattedEnd) {
      return null;
    }

    return `${formattedStart} to ${formattedEnd}`;
  }, [availabilityEndTime, availabilityStartTime, effectiveTimeZone]);
  const minimumStart = !allowOutsideAvailabilityWindow && availabilityStartInput && availabilityStartInput > currentMinuteInput
    ? availabilityStartInput
    : currentMinuteInput;
  const durationMinutes = useMemo(() => {
    if (!initialStartTime || !initialEndTime) {
      return null;
    }

    const durationMs = new Date(initialEndTime).getTime() - new Date(initialStartTime).getTime();
    if (!Number.isFinite(durationMs) || durationMs <= 0) {
      return null;
    }

    return Math.round(durationMs / 60000);
  }, [initialEndTime, initialStartTime]);
  const computedEndTime = useMemo(() => {
    const proposedStartTime = toZonedIsoString(startTime, effectiveTimeZone);
    if (!proposedStartTime || durationMinutes === null) {
      return null;
    }

    return new Date(new Date(proposedStartTime).getTime() + durationMinutes * 60_000).toISOString();
  }, [durationMinutes, effectiveTimeZone, startTime]);
  const computedEndInput = useMemo(
    () => toDateTimeLocalInputInTimeZone(computedEndTime, effectiveTimeZone),
    [computedEndTime, effectiveTimeZone],
  );
  const computedEndLabel = useMemo(
    () => formatDateTimeInTimeZone(computedEndTime, effectiveTimeZone, null),
    [computedEndTime, effectiveTimeZone],
  );
  const durationLabel = useMemo(() => {
    if (durationMinutes === null) {
      return "Duration unavailable";
    }

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    if (hours === 0) {
      return `${minutes} min`;
    }
    if (minutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${minutes} min`;
  }, [durationMinutes]);
  const maximumStart = useMemo(() => {
    if (allowOutsideAvailabilityWindow) {
      return undefined;
    }

    return availabilityEndInput || undefined;
  }, [allowOutsideAvailabilityWindow, availabilityEndInput]);

  const handleSubmit = async () => {
    const proposedStartTime = toZonedIsoString(startTime, effectiveTimeZone);
    const proposedEndTime = computedEndTime;

    if (!proposedStartTime || !proposedEndTime) {
      setError(durationMinutes === null ? "Unable to calculate the ending time for this proposal." : "Choose a start time.");
      return;
    }

    if (new Date(proposedEndTime).getTime() <= new Date(proposedStartTime).getTime()) {
      setError("The calculated end time must be after the start time.");
      return;
    }

    if (!allowOutsideAvailabilityWindow
      && availabilityStartTime
      && new Date(proposedStartTime).getTime() < new Date(availabilityStartTime).getTime()) {
      setError("Proposed start time must be within the gig availability window.");
      return;
    }

    if (!allowOutsideAvailabilityWindow
      && availabilityEndTime
      && new Date(proposedStartTime).getTime() > new Date(availabilityEndTime).getTime()) {
      setError("Proposed start time must be within the gig availability window.");
      return;
    }

    setError(null);
    await onSubmit({
      note: null,
      proposedStartTime,
      proposedEndTime,
    });
  };

  return (
    <div className="space-y-5">
      <p className="text-sm leading-6 text-myteengig-text-muted">{subtitle}</p>
      {availabilityWindowLabel ? (
        <p className="text-sm text-myteengig-text-muted">
          {allowOutsideAvailabilityWindow
            ? `Original gig window: ${availabilityWindowLabel}.`
            : `Choose a start time inside the gig window: ${availabilityWindowLabel}.`}
        </p>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <label className="grid gap-2 text-sm font-medium text-myteengig-text-primary">
          Proposed start
          <input
            type="datetime-local"
            value={startTime}
            min={minimumStart}
            max={maximumStart}
            step={60}
            onChange={(event) => {
              setStartTime(event.target.value);
              setError(null);
            }}
            className="rounded-2xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary"
          />
        </label>

        <div className="grid gap-2 text-sm font-medium text-myteengig-text-primary">
          <span>Ending time</span>
          <div className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-3 text-sm text-myteengig-text-primary">
            <p className="font-medium">{computedEndLabel ?? "Choose a start time to calculate the end."}</p>
            <p className="mt-1 text-xs text-myteengig-text-muted">
              Based on the gig duration of {durationLabel}.
            </p>
          </div>
          <input type="hidden" value={computedEndInput} readOnly />
        </div>
      </div>

      {error ? <p className="text-sm font-medium text-red-600">{error}</p> : null}

      <div className="flex flex-wrap justify-end gap-3">
        {onCancel ? (
          <Button variant="outline" disabled={isSubmitting} onClick={onCancel}>
            Keep Current Time
          </Button>
        ) : null}
        <Button disabled={isSubmitting} onClick={() => void handleSubmit()}>
          {isSubmitting ? "Sending..." : "Send New Time"}
        </Button>
      </div>
    </div>
  );
};

const ScheduleProposalModal = ({
  isOpen,
  title,
  onClose,
  ...formProps
}: ScheduleProposalModalProps) => (
  <Modal isOpen={isOpen} onClose={() => !formProps.isSubmitting && onClose()} title={title}>
    <ScheduleProposalForm {...formProps} onCancel={onClose} />
  </Modal>
);

export default ScheduleProposalModal;
